import { createSlice } from "@reduxjs/toolkit";

const initialState = {
    /* The check currently open in the dashboard — null means show the list */
    activeCheck:   null,

    /* Live state for the check being viewed */
    jobId:         null,
    jobStatus:     null,     // RUNNING | COMPLETED | FAILED
    name:          null,
    fileName:      null,
    totalRows:     0,
    processedRows: 0,
    results:       [],

    /* Sharing — who owns this check and who else can open it */
    ownerId:      null,
    ownerEmail:   null,
    genaiRightsAccess: [],   // [{ user_id, email }]
};

const genaiRightsSlice = createSlice({
    name: "genaiRights",
    initialState,
    reducers: {
        /* Opening a saved check from the list */
        setActiveCheck: (state, action) => {
            const c = action.payload || {};
            state.activeCheck   = c;
            state.jobId         = c.id ?? c.job_id ?? null;
            state.jobStatus     = c.status ?? null;
            state.name          = c.name ?? null;
            state.fileName      = c.file_name ?? null;
            state.totalRows     = c.total_rows ?? 0;
            state.processedRows = c.processed_rows ?? 0;
            state.results       = [];
            state.ownerId       = c.user_id ?? c.owner_id ?? null;
            state.ownerEmail    = c.owner ?? c.owner_email ?? null;
            state.genaiRightsAccess = c.genai_rights_access ?? [];
        },

        clearActiveCheck: () => initialState,

        /* A freshly uploaded file has started processing */
        setJobStarted: (state, action) => {
            const p = action.payload || {};
            state.activeCheck   = { id: p.jobId ?? p.job_id, name: p.name, status: "RUNNING" };
            state.jobId         = p.jobId ?? p.job_id ?? null;
            state.jobStatus     = "RUNNING";
            state.name          = p.name ?? null;
            state.fileName      = p.fileName ?? p.file_name ?? null;
            state.totalRows     = p.totalRows ?? p.total_rows ?? 0;
            state.processedRows = 0;
            state.results       = [];
            state.ownerId       = null;
            state.ownerEmail    = null;
            state.genaiRightsAccess = [];
        },

        /* Progress-only update — leaves status and results alone.
           Accepts snake_case (from SSE) or camelCase payloads. */
        setProgress: (state, action) => {
            const p = action.payload || {};
            const processed = p.processed_rows ?? p.processedRows;
            const total     = p.total_rows     ?? p.totalRows;
            if (processed !== undefined && processed !== null) state.processedRows = processed;
            if (total     !== undefined && total     !== null) state.totalRows     = total;
        },

        /* Full update once results are available */
        updatePollData: (state, action) => {
            const p = action.payload || {};
            if (p.status        !== undefined) state.jobStatus     = p.status;
            if (p.name          !== undefined) state.name          = p.name;
            if (p.fileName      !== undefined) state.fileName      = p.fileName;
            if (p.totalRows     !== undefined) state.totalRows     = p.totalRows;
            if (p.processedRows !== undefined) state.processedRows = p.processedRows;
            if (p.results       !== undefined) state.results       = p.results;
            if (p.ownerId       !== undefined) state.ownerId       = p.ownerId;
            if (p.ownerEmail    !== undefined) state.ownerEmail    = p.ownerEmail;
            if (p.genaiRightsAccess !== undefined) state.genaiRightsAccess = p.genaiRightsAccess;
        },

        setUploadFailed: (state) => {
            state.jobStatus = "FAILED";
        },

        updateRowStatus: (state, action) => {
            const { row_num, ...changes } = action.payload || {};
            const row = state.results.find((r) => r.row_num === row_num);
            if (row) Object.assign(row, changes);
        },

        /* After Save Access succeeds — update sharing locally without a refetch */
        setGenaiRightsAccess: (state, action) => {
            state.genaiRightsAccess = action.payload || [];
        },

        resetGenAIRightsState: () => initialState,
    },
});

export const {
    setActiveCheck,
    clearActiveCheck,
    setJobStarted,
    setProgress,
    updatePollData,
    setUploadFailed,
    updateRowStatus,
    setGenaiRightsAccess,
    resetGenAIRightsState,
} = genaiRightsSlice.actions;

export default genaiRightsSlice.reducer;